/**
 * SSR-safe helpers around localStorage. All operations are wrapped so they
 * simply no-op (for setters) or return fallbacks (for getters) when executed
 * in a non-browser environment (e.g. during Next.js SSR or in Docker builds).
 */

export function hasWindow(): boolean {
  return typeof window !== 'undefined' && window.localStorage !== undefined;
}

export function lsGet(key: string): string | null {
  if (!hasWindow()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function lsSet(key: string, value: string): void {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

export function lsRemove(key: string): void {
  if (!hasWindow()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function lsJsonGet<T>(key: string, fallback: T): T {
  const raw = lsGet(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function lsJsonSet(key: string, value: unknown): void {
  try {
    lsSet(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}
